/**
 * The reader's box on the website and in Android's web view: truman's browser
 * library in this page, its files in the origin's private file system under the
 * reader's namespace. truman loads on the first call, so a page that never
 * touches the box never downloads it.
 */
import type { ExecOptions, WebTruman } from "@heydaytime/truman-web";
import { BoxError, type BoxErrorCode, type BoxHost, boxErrorCode } from "@heydex/shared/box";

import { networkTransport } from "./network";

/** The OPFS folder that holds a reader's box. */
export const boxNamespace = (readerKey: string): string => `heydex-${readerKey}`;

/** truman's runtime folder, served beside HeyDex. */
const ASSETS = `${import.meta.env.BASE_URL}truman/`;

/** truman's own words for a call its page ended mid-way: its effect is unknown. */
const LOST = new Set(["the box worker stopped", "this box was closed"]);

/** truman's own words for a box that could not start. */
const STARTING = [
  /^this box is closed$/,
  /^the box worker stopped while starting$/,
  /^the box namespace ".+" is open in another page or WebTruman$/,
  /^truman's runtime is not (reachable|served) at /,
];

const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

function code(message: string): BoxErrorCode {
  if (LOST.has(message)) return "lost";
  if (STARTING.some((pattern) => pattern.test(message))) return "unavailable";
  // truman's file system names the POSIX code first: "ENOENT: no such file or directory, stat '/x'".
  return boxErrorCode(/^([A-Z]+): /.exec(message)?.[1]);
}

const asBoxError = (error: unknown): BoxError =>
  error instanceof BoxError ? error : new BoxError(code(messageOf(error)), messageOf(error));

export function webBox(readerKey: string): { host: BoxHost; close(): Promise<void> } {
  let started: Promise<WebTruman> | null = null;
  let closeNetwork: (() => void) | null = null;
  const box = (): Promise<WebTruman> =>
    (started ??= import("@heydaytime/truman-web").then(
      async ({ WebTruman, hostNetwork }) => {
        const network = await networkTransport();
        closeNetwork = network.close;
        try {
          return new WebTruman({
            namespace: boxNamespace(readerKey),
            assets: ASSETS,
            network: hostNetwork(network.transport),
          });
        } catch (error) {
          network.close();
          throw error;
        }
      },
      (error: unknown) => {
        throw new BoxError("unavailable", `the box could not load: ${messageOf(error)}`);
      },
    ));
  const file = async <T>(operation: (truman: WebTruman) => Promise<T>): Promise<T> => {
    const truman = await box();
    try {
      return await operation(truman);
    } catch (error) {
      throw asBoxError(error);
    }
  };

  const host: BoxHost = {
    exec: async (command, options = {}) => {
      const truman = await box();
      const sent: ExecOptions = {
        ...(options.cwd !== undefined ? { cwd: options.cwd } : {}),
        ...(options.env !== undefined ? { env: { ...options.env } } : {}),
        ...(options.stdin !== undefined ? { stdin: options.stdin } : {}),
        ...(options.deadlineMs !== undefined ? { deadlineMs: options.deadlineMs } : {}),
      };
      // A command's own failure is its exit status and stderr; truman rejects only when the box is gone.
      const result = await truman.exec(command, sent).catch((error: unknown) => {
        const message = messageOf(error);
        throw new BoxError(LOST.has(message) ? "lost" : "unavailable", message);
      });
      return {
        stdout: result.stdout,
        stderr: result.stderr,
        exitCode: result.exitCode,
        cwd: result.cwd,
        env: result.env,
        timedOut: result.timedOut === true,
        truncated: result.truncated === true,
        lost: result.lost === true,
      };
    },
    interrupt: async () => {
      if (started) (await started).interrupt();
    },
    readFile: (path) => file((truman) => truman.readFile(path)),
    writeFile: (path, content) => file((truman) => truman.writeFile(path, content)),
    stat: (path) =>
      file(async (truman) => {
        const stat = await truman.stat(path);
        return {
          isFile: stat.isFile,
          isDirectory: stat.isDirectory,
          isSymbolicLink: stat.isSymbolicLink,
          size: stat.size,
          mode: stat.mode,
          mtimeMs: stat.mtime.getTime(),
        };
      }),
    listDir: (path) =>
      file(async (truman) =>
        (await truman.listDir(path)).map(({ name, isFile, isDirectory, isSymbolicLink }) => ({
          name,
          isFile,
          isDirectory,
          isSymbolicLink,
        })),
      ),
    mkdir: (path, { recursive }) => file((truman) => truman.mkdir(path, { recursive })),
    remove: (path, { recursive, force }) =>
      file((truman) => truman.remove(path, { recursive, force })),
    move: (from, to) => file((truman) => truman.move(from, to)),
  };

  return {
    host,
    close: async () => {
      const opened = await started?.catch(() => null);
      try {
        await opened?.close();
      } finally {
        closeNetwork?.();
      }
    },
  };
}

/** Deletes a reader's box files; the box must be closed. */
export async function deleteWebBox(readerKey: string): Promise<void> {
  const { WebTruman } = await import("@heydaytime/truman-web");
  await WebTruman.deleteNamespace(boxNamespace(readerKey));
}
